import React, { useState, useEffect, useCallback } from 'react';import api from '../../services/api';
import { withUploadToken } from '../../utils/fileUrl';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Alert,
  FormControl,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatDateTimeInput } from '../../utils/dateUtils';
import { FREQ_OPTIONS, computeLine, scheduleText, perDayOf, codeFromPerDay } from '../../utils/medSchedule';
import canViewPrices from '../../utils/pricing';

const apiUrl = process.env.REACT_APP_API_URL;

const VetClinicRecordEdit = () => {
  const { t } = useTranslation();
  const { record_id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    record_id: '',
    payment_type_id: '',
    payment_type: '',
    record_date: '',
    horse_id: '',
    outside_horse_id: '',
    inside_horse_id: '',
    rfid_chip_number: '',
    horse_name: '',
    total_amount: '',
    work_place: '',
    file_upload: '',
    medical_history: '',
    diagnosis: '',
    treatment: '',
    notes: '',
    payment_type_name: '',
    approval: 0,
    client_id: '',
    owner_id: '',
    user_id: '',
    medications: [],
    procedures: [],
    file_uploads: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [medications, setMedications] = useState([]);
  const [procedures, setProcedures] = useState([]);

  const calculateTotalAmount = useCallback(() => {
    const proceduresTotal = formData.procedures.reduce((sum, proc) => {
      return sum + (proc.price || 0);
    }, 0);

    // تكلفة سطر الدواء = سعر الوحدة × إجمالي الوحدات (المحسوبة من الجدول).
    const medicationsTotal = formData.medications.reduce(
      (sum, med) => sum + computeLine(med).cost,
      0
    );

    setFormData((prevState) => ({
      ...prevState,
      total_amount: proceduresTotal + medicationsTotal,
    }));
  }, [formData.procedures, formData.medications]);

  useEffect(() => {
    calculateTotalAmount();
  }, [formData.procedures, formData.medications, calculateTotalAmount]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    const uploadPromises = files.map((file) => {
      const formData = new FormData();
      formData.append('file', file);

      return api.post(`${apiUrl}/vetclinic-records/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    });

    try {
      const responses = await Promise.all(uploadPromises);
      const uploadedFiles = responses.map((response) => response.data.filePath);

      setFormData((prevState) => ({
        ...prevState,
        file_uploads: [...prevState.file_uploads, ...uploadedFiles],
      }));
    } catch (error) {
      console.error('Error uploading files:', error);
    }
  };

  // إزالة صورة من التقرير فقط (تُفصل عن السجل عند الحفظ، ويبقى الملف محفوظًا على القرص).
  const removeFile = (index) => {
    setFormData((prevState) => ({
      ...prevState,
      file_uploads: prevState.file_uploads.filter((_, i) => i !== index),
    }));
  };

  useEffect(() => {
    //console.log('Form Data:', formData); // Logging the form data
  }, [formData]);
  const fetchRecordDetails = useCallback(async () => {
    try {
      const response = await api.get(
        `${apiUrl}/vetclinic-records/${record_id}`,
        {
          headers: {
            Authorization: 'your_jwt_secret_key',
          },
        }
      );

      const recordData = response.data;
      if (recordData.record_date) {
        setFormData((prevData) => ({
          ...prevData,
          record_id: recordData.record_id || '',
          payment_type_id: recordData.payment_type_id || '',
          payment_type: recordData.payment_type || '',
          record_date: formatDateTimeInput(recordData.record_date),
          horse_id: recordData.horse_id || '',
          outside_horse_id: recordData.outside_horse_id || '',
          inside_horse_id: recordData.inside_horse_id || '',
          rfid_chip_number: recordData.rfid_chip_number || '',
          horse_name: recordData.horse_name || '',
          total_amount: recordData.total_amount || '',
          work_place: recordData.work_place || '',
          file_upload: recordData.file_upload || '',
          // تحميل الملفات الموجودة (تأتي من جدول vetclinic_files كسلسلة مفصولة بـ;)
          file_uploads: Array.isArray(recordData.file_uploads)
            ? recordData.file_uploads
            : typeof recordData.file_uploads === 'string'
              ? recordData.file_uploads.split(';').filter(Boolean)
              : [],
          medical_history: recordData.medical_history || '',
          diagnosis: recordData.diagnosis || '',
          treatment: recordData.treatment || '',
          notes: recordData.notes || '',
          payment_type_name: recordData.payment_type_name || '',
          approval: recordData.approval || 0,
          client_id: recordData.client_id || '',
          owner_id: recordData.owner_id || '',
          quantity: recordData.quantity || '',
          user_id: recordData.user_id || '',
        }));
      }

      const medicationsResponse = await api.get(
        `${apiUrl}/medications-records/${record_id}`
      );
      // إعادة بناء جدول الجرعة من القيم المخزّنة (freq_per_day → سيناريو، duration_days → مدّة).
      const meds = (medicationsResponse.data || []).map((m) => ({
        ...m,
        unit: m.unit || m.default_unit || '',
        pricingMode: m.pricing_mode === 'flat' ? 'flat' : 'per_unit',
        freqCode: codeFromPerDay(m.freq_per_day) || 'qd',
        durationDays: m.duration_days ?? '',
      }));
      setFormData((prevData) => ({
        ...prevData,
        medications: meds,
      }));

      const proceduresResponse = await api.get(
        `${apiUrl}/procedures-records/${record_id}`
      );
      setFormData((prevData) => ({
        ...prevData,
        procedures: proceduresResponse.data,
      }));

      setLoading(false);
    } catch (err) {
      console.error('Error fetching record details:', err);
      setError('Error fetching record details.');
      setLoading(false);
    }
  }, [record_id]);

  useEffect(() => {
    fetchRecordDetails();
    fetchDropdownData();
  }, [record_id, fetchRecordDetails]);

  const fetchDropdownData = async () => {
    try {
      const [medicationsResponse, proceduresResponse] = await Promise.all([
        api.get(`${apiUrl}/medications`),
        api.get(`${apiUrl}/procedures`),
      ]);

      setMedications(medicationsResponse.data);
      setProcedures(proceduresResponse.data);
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
      setError('Error fetching dropdown data.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleMedicationChange = (index, field, value) => {
    setFormData((prevState) => {
      const updatedMedications = prevState.medications.map((med, i) => {
        if (i === index) {
          const updatedMed = { ...med, [field]: value };

          if (field === 'medication_id') {
            const selectedMed = medications.find(
              (m) => m.medication_id === parseInt(value)
            );
            if (selectedMed) {
              // pricing_mode='flat' (3.4.87 — مثل المغذّي): التكرار والمدّة يبقيان كما هما،
              // فقط "الجرعة/مرّة" غير معروضة (تُفرَض 1 داخليًّا في computeLine).
              const pricingMode = selectedMed.pricing_mode === 'flat' ? 'flat' : 'per_unit';
              const defDays = parseInt(String(selectedMed.default_duration || '').match(/\d+/)?.[0] || '', 10);
              updatedMed.unit = selectedMed.default_unit || ''; // ثابتة من المخزون
              updatedMed.price = selectedMed.price || 0;
              updatedMed.pricingMode = pricingMode;
              updatedMed.dosage = selectedMed.default_dosage || '';
              updatedMed.freqCode = updatedMed.freqCode || 'qd';
              updatedMed.durationDays = updatedMed.durationDays || (Number.isNaN(defDays) ? '' : defDays);
            }
          }

          return updatedMed;
        }
        return med;
      });

      return { ...prevState, medications: updatedMedications };
    });
  };

  // Add price when changing procedures
  const handleProcedureChange = (procedure_id) => {
    setFormData((prevState) => {
      const proc = procedures.find((p) => p.procedure_id === procedure_id);
      if (!proc) return prevState;

      const updatedProcedures = prevState.procedures.some(
        (p) => p.procedure_id === procedure_id
      )
        ? prevState.procedures.filter((p) => p.procedure_id !== procedure_id)
        : [...prevState.procedures, { ...proc, price: proc.price || 0 }];

      return { ...prevState, procedures: updatedProcedures };
    });
  };

  const addMedication = () => {
    setFormData((prevData) => ({
      ...prevData,
      medications: [
        ...prevData.medications,
        { medication_id: '', dosage: '', unit: '', price: 0, freqCode: 'qd', durationDays: '' },
      ],
    }));
  };

  const removeMedication = (index) => {
    const newMedications = formData.medications.filter((_, i) => i !== index);
    setFormData((prevData) => ({ ...prevData, medications: newMedications }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    //console.log('تم الضغط على زر الحفظ');
    //console.log('بيانات النموذج:', formData);

    /*
    if (!formData.payment_type_id) {
      setError('Payment type is required.');
      return;
    }*/

    const sanitizedFormData = {
      ...formData,
      horse_id: formData.horse_id || null,
      file_upload: formData.file_upload || null,
      payment_type_id: formData.payment_type_id || null,
      client_id: formData.client_id || null,
      owner_id: formData.owner_id || null,
      inside_horse_id: formData.inside_horse_id || null,
      outside_horse_id: formData.outside_horse_id || null,
      notes: formData.notes || null,
      medical_history: formData.medical_history || null,
      diagnosis: formData.diagnosis || null,
      treatment: formData.treatment || null,
      procedures: formData.procedures, // أضف هذه الخاصية
      // quantity = إجمالي الوحدات؛ price = سعر الوحدة؛ + جدول الجرعة لتدويره لاحقًا.
      medications: (formData.medications || [])
        .filter((med) => med.medication_id)
        .map((med) => {
          const line = computeLine(med);
          return {
            medication_id: med.medication_id,
            dosage: med.dosage,
            quantity: line.totalUnits,
            unit: med.unit,
            duration: scheduleText(med, t),
            price: line.price,
            freq_per_day: perDayOf(med.freqCode),
            duration_days: parseInt(med.durationDays, 10) || null,
          };
        }),
    };

    try {
      //console.log('Sanitized Data Sent:', sanitizedFormData); // أضف هذه السطر للطباعة
      const token = localStorage.getItem('token'); // أو من context حسب نظامك

      await api.put(
        `${apiUrl}/vetclinic-records-edit/${record_id}`,
        sanitizedFormData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      //console.log('Backend Response:', response.data); // طباعة استجابة الـ backend
      navigate(`/record-details/${record_id}`);
    } catch (err) {
      console.error('Failed to update record:', err.response.data); // الطباعة هنا
      setError(err.response.data.error || 'Error updating record.');
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          marginTop: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h5">
          Edit Vet Clinic Record
        </Typography>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="record_date"
                label="Record Date"
                type="datetime-local"
                fullWidth
                value={formData.record_date}
                InputLabelProps={{
                  shrink: true,
                }}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="work_place"
                label="Work Place"
                fullWidth
                value={formData.work_place}
                onChange={handleChange}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="rfid_chip_number"
                label="RFID Chip Number"
                fullWidth
                value={formData.rfid_chip_number}
                onChange={handleChange}
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="horse_name"
                label="Horse Name"
                fullWidth
                value={formData.horse_name}
                onChange={handleChange}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="user_id"
                label="User ID"
                type="number"
                fullWidth
                value={formData.user_id}
                onChange={handleChange}
                disabled
              />
            </Grid>

            {false && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="total_amount"
                    label="Total Amount"
                    type="number"
                    fullWidth
                    value={formData.total_amount}
                    onChange={handleChange}
                    disabled
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <FormControl component="fieldset" margin="normal">
                <Typography>Procedures</Typography>
                <Grid container spacing={2}>
                  {procedures.map((procedure) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      key={procedure.procedure_id}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            value={procedure.procedure_id}
                            checked={formData.procedures.some(
                              (proc) =>
                                String(proc.procedure_id) ===
                                String(procedure.procedure_id)
                            )}
                            onChange={() =>
                              handleProcedureChange(procedure.procedure_id)
                            }
                          />
                        }
                        label={procedure.procedure_name}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="medical_history"
                label="Medical History"
                multiline
                rows={4}
                fullWidth
                value={formData.medical_history}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="diagnosis"
                label="Diagnosis"
                multiline
                rows={4}
                fullWidth
                value={formData.diagnosis}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="treatment"
                label="Treatment"
                multiline
                rows={4}
                fullWidth
                value={formData.treatment}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Notes"
                multiline
                rows={4}
                fullWidth
                value={formData.notes || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl
                component="fieldset"
                margin="dense"
                style={{ display: 'flex', flexWrap: 'wrap' }}
              >
                <Typography>Medications</Typography>
                {formData.medications.map((medication, index) => (
                  <div key={index} style={{ marginBottom: '10px' }}>
                    <Autocomplete
                      options={medications}
                      value={medications.find((med) => String(med.medication_id) === String(medication.medication_id)) || null}
                      onChange={(_, selectedMedication) =>
                        handleMedicationChange(index, 'medication_id', selectedMedication?.medication_id || '')
                      }
                      isOptionEqualToValue={(option, value) => String(option.medication_id) === String(value.medication_id)}
                      getOptionLabel={(option) =>
                        option?.medication_name
                          ? `${option.medication_name} - ${t('stock_quantity')}: ${option.stock_quantity ?? 0}`
                          : ''
                      }
                      getOptionDisabled={(option) => Number(option.stock_quantity || 0) <= 0}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('medication_name')}
                          margin="normal"
                          fullWidth
                        />
                      )}
                      fullWidth
                    />

                    <Grid container spacing={1.5} alignItems="flex-start">
                      <Grid item xs={6} sm={3}>
                        <TextField
                          fullWidth
                          label={t('clinicMed.dosePerTime')}
                          type="number"
                          value={medication.pricingMode === 'flat' ? '' : medication.dosage}
                          onChange={(e) => handleMedicationChange(index, 'dosage', e.target.value)}
                          margin="dense"
                          disabled={medication.pricingMode === 'flat'}
                          placeholder={medication.pricingMode === 'flat' ? '—' : undefined}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          fullWidth
                          label={t('clinicMed.unit')}
                          value={medication.unit || ''}
                          margin="dense"
                          InputProps={{ readOnly: true }}
                          disabled
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <FormControl fullWidth margin="dense">
                          <InputLabel>{t('clinicMed.frequency')}</InputLabel>
                          <Select
                            label={t('clinicMed.frequency')}
                            value={medication.freqCode || 'qd'}
                            onChange={(e) => handleMedicationChange(index, 'freqCode', e.target.value)}
                          >
                            {FREQ_OPTIONS.map((o) => (
                              <MenuItem key={o.code} value={o.code}>{t(o.labelKey)}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField
                          fullWidth
                          label={t('clinicMed.durationDays')}
                          type="number"
                          value={medication.durationDays ?? ''}
                          onChange={(e) => handleMedicationChange(index, 'durationDays', e.target.value)}
                          margin="dense"
                        />
                      </Grid>
                    </Grid>
                    <div style={{ marginBottom: 6 }}>
                      {[['clinicMed.week', 7], ['clinicMed.fortnight', 14], ['clinicMed.month', 30]].map(([key, d]) => (
                        <Button key={d} size="small" variant="outlined" style={{ marginInlineEnd: 6 }}
                          onClick={() => handleMedicationChange(index, 'durationDays', d)}>
                          {t(key)}
                        </Button>
                      ))}
                    </div>
                    {canViewPrices() && (() => {
                      const c = computeLine(medication);
                      return (
                        <div style={{ background: '#f6f3ea', borderRadius: 8, padding: '8px 12px', fontSize: 14, marginBottom: 8 }}>
                          {t('clinicMed.administrations')}: <b>{c.administrations}</b>{'  ·  '}
                          {t('clinicMed.totalQty')}: <b>{c.totalUnits} {medication.unit}</b>{'  ·  '}
                          {t('clinicMed.unitPrice')}: <b>{c.price}</b>{'  ·  '}
                          {t('clinicMed.cost')}: <b>{c.cost}</b>
                        </div>
                      );
                    })()}
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => removeMedication(index)}
                    >
                      Remove Medication
                    </Button>
                  </div>
                ))}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={addMedication}
                >
                  Add Medication
                </Button>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6">Upload Files</Typography>
              <input type="file" multiple onChange={handleFileUpload} />
            </Grid>

            {formData.file_uploads.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="h6">{t('uploaded_files') || 'Uploaded Files'}</Typography>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  {formData.file_uploads.map((file, index) => {
                    const isImage = /\.(png|jpe?g|gif|webp|bmp|heic)$/i.test(file);
                    return (
                      <div
                        key={index}
                        style={{
                          position: 'relative',
                          width: 110,
                          border: '1px solid #ddd',
                          borderRadius: 6,
                          padding: 6,
                          textAlign: 'center',
                          background: '#fafafa',
                        }}
                      >
                        <a href={withUploadToken(`${apiUrl}/uploads/${file}`)} target="_blank" rel="noreferrer">
                          {isImage ? (
                            <img
                              src={withUploadToken(`${apiUrl}/uploads/${file}`)}
                              alt={file}
                              style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 4 }}
                            />
                          ) : (
                            <div style={{ fontSize: 12, wordBreak: 'break-all', padding: 4 }}>{file}</div>
                          )}
                        </a>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => removeFile(index)}
                          style={{ marginTop: 4, minWidth: 0, padding: '2px 8px' }}
                          aria-label={t('remove') || 'Remove'}
                        >
                          {t('remove') || 'Remove'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </Grid>
            )}

            <Grid
              item
              xs={12}
              style={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                style={{ marginRight: '10px' }}
              >
                Save Changes
              </Button>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
};

export default VetClinicRecordEdit;
